// Every template the Studio can start from, in one dialog: the built-in templates of each
// family (one tab per family) and the templates saved on this device, in my account or
// shared by other creators. Opening one goes through the Studio's own open paths, which ask
// before replacing unsaved work and report their own errors.
import { templateCard } from './studio-families.mjs';

const LOCATION = { device: '이 기기', account: '내 계정', shared: '공유' };

function node(tag, attributes = {}, children = []) {
  const element = document.createElement(tag);
  for (const [name, value] of Object.entries(attributes)) {
    if (name === 'text') element.textContent = value;
    else element.setAttribute(name, value);
  }
  element.append(...children);
  return element;
}

function savedMeta(record) {
  const date = record.updatedAt ? new Date(record.updatedAt).toLocaleDateString('ko-KR') : '';
  return [LOCATION[record.location], record.location === 'shared' && !record.mine ? '다른 제작자' : '', date].filter(Boolean).join(' · ');
}

/**
 * families: the presets, [{ id, label, description, examples: [{ id, label, … }] }].
 * listSaved(): resolves the saved templates, [{ id, title, location, mine, updatedAt }].
 * openBuiltin(id), openSaved(id): open one template as new content.
 * pageCountsOf(card): resolves a built-in template's page counts — { book, templates } for one edited on its book's
 * pages (its pages and page templates), else { studio } (its Studio pages).
 */
export function mountTemplatePicker({ families, listSaved, openBuiltin, openSaved, pageCountsOf = null }) {
  const builtinCards = families.flatMap(family => family.examples.map(example =>
    ({ kind: 'builtin', id: example.id, familyId: family.id, example, ...templateCard(family.id, example) })));
  const pageCounts = new Map();
  const describeCounts = ({ studio, book, templates }) => (book ? `책 ${book}쪽 · 쪽 틀 ${templates}개` : `제작실 ${studio}쪽`);
  let saved = [], savedError = null, tab = 'all';

  const search = node('input', { type: 'search', placeholder: '제목이나 설명으로 찾기', 'aria-label': '템플릿 찾기', 'data-testid': 'studio-template-search' });
  const tabs = node('div', { class: 'es-template-picker-tabs', role: 'tablist', 'aria-label': '템플릿 종류' });
  const body = node('div', { class: 'es-template-picker-body' });
  const empty = node('p', { class: 'es-template-picker-empty', hidden: '' });
  const close = node('button', { type: 'button', class: 'es-template-picker-close', text: '닫기' });
  const dialog = node('dialog', { class: 'es-template-picker', 'aria-labelledby': 'es-template-picker-title', 'data-testid': 'studio-template-picker' }, [
    node('header', {}, [node('h2', { id: 'es-template-picker-title', text: '템플릿' }),
      node('p', { text: '고른 템플릿으로 새 콘텐츠를 만듭니다. 저장하지 않은 작업이 있으면 먼저 묻습니다.' }), close, search, tabs]),
    body, empty,
  ]);
  close.addEventListener('click', () => dialog.close());
  dialog.addEventListener('click', event => { if (event.target === dialog) dialog.close(); });
  search.addEventListener('input', () => render());

  function groups() {
    return [
      ...families.map(family => ({ id: family.id, label: family.label, description: family.description ?? '', cards: builtinCards.filter(card => card.familyId === family.id) })),
      { id: 'saved', label: '저장한 템플릿', description: savedError ?? '이 기기나 내 계정에 저장한 템플릿, 다른 제작자가 공유한 템플릿입니다.',
        cards: saved.map(record => ({ kind: 'saved', id: record.id, familyId: 'saved', title: record.title, description: '', meta: savedMeta(record) })) },
    ];
  }

  function cardButton(card) {
    const button = node('button', { type: 'button', class: 'es-template-picker-card', 'data-family': card.familyId, 'data-testid': `studio-template-card-${card.id}`,
      'aria-label': `${card.title} 템플릿 열기` }, [
      node('strong', { text: card.title }),
      ...(card.description ? [node('span', { class: 'es-template-picker-description', text: card.description })] : []),
      ...(card.meta ? [node('small', { text: card.meta })] : []),
      ...(card.kind === 'builtin' ? [node('small', { class: 'es-template-picker-pages', 'data-testid': `studio-template-pages-${card.id}`,
        text: pageCounts.has(card.id) ? describeCounts(pageCounts.get(card.id)) : '' })] : []),
    ]);
    button.addEventListener('click', async () => {
      dialog.close();
      await (card.kind === 'saved' ? openSaved(card.id) : openBuiltin(card.id));
    });
    return button;
  }

  function render() {
    const query = search.value.trim();
    const terms = query.toLocaleLowerCase().split(/\s+/).filter(Boolean);
    const matches = card => { const text = `${card.title} ${card.description} ${card.meta}`.toLocaleLowerCase(); return terms.every(term => text.includes(term)); };
    const all = groups();
    const entries = [{ id: 'all', label: '전체', count: all.reduce((sum, group) => sum + group.cards.length, 0) },
      ...all.map(group => ({ id: group.id, label: group.label, count: group.cards.length }))];
    tabs.replaceChildren(...entries.map(entry => {
      const button = node('button', { type: 'button', role: 'tab', 'aria-selected': String(entry.id === tab), 'data-testid': `studio-template-tab-${entry.id}` },
        [node('span', { text: entry.label }), node('span', { class: 'es-template-picker-count', text: String(entry.count) })]);
      button.addEventListener('click', () => { tab = entry.id; render(); });
      return button;
    }));
    let shown = 0;
    body.replaceChildren(...all.filter(group => tab === 'all' || group.id === tab).flatMap(group => {
      const cards = group.cards.filter(matches);
      shown += cards.length;
      if (!cards.length) return [];
      return [node('section', { class: 'es-template-picker-group', 'data-family': group.id }, [
        node('h3', { text: group.label }), node('p', { text: group.description }),
        node('div', { class: 'es-template-picker-grid' }, cards.map(cardButton)),
      ])];
    }));
    empty.hidden = shown > 0;
    empty.textContent = query ? `“${query}”에 맞는 템플릿이 없습니다.`
      : tab === 'saved' ? (savedError ?? '저장한 템플릿이 아직 없습니다. 자료 패널에서 현재 콘텐츠를 템플릿으로 저장할 수 있습니다.')
      : '이 종류에는 아직 템플릿이 없습니다.';
  }

  /** Opens the dialog; `tab` picks a family ('all', a family id or 'saved'). */
  async function show({ tab: initial } = {}) {
    if (initial) tab = initial;
    search.value = '';
    try { saved = await listSaved(); savedError = null; }
    catch (error) { saved = []; savedError = `저장한 템플릿을 불러오지 못했습니다: ${error.message}`; }
    render();
    // Page counts come in as they are known (the book's needs the published page list).
    if (pageCountsOf && pageCounts.size < builtinCards.length) {
      void Promise.all(builtinCards.filter(card => !pageCounts.has(card.id)).map(async card => {
        const counts = await pageCountsOf(card);
        pageCounts.set(card.id, counts);
        const slot = body.querySelector(`[data-testid="studio-template-pages-${CSS.escape(card.id)}"]`);
        if (slot) slot.textContent = describeCounts(counts);
      })).catch(() => {});
    }
    if (!dialog.isConnected) document.body.append(dialog);
    if (!dialog.open) dialog.showModal();
    search.focus();
  }

  const trigger = node('button', { type: 'button', class: 'es-template-picker-open', 'aria-haspopup': 'dialog', 'data-testid': 'studio-template-picker-open', text: '템플릿' });
  return { trigger, dialog, show };
}
